<template>
  <span v-if="!status" class="kf-dash">-</span>
  <span
    v-else
    class="kf-chip"
    :class="{ 'kf-chip--sm': small }"
    :style="chipStyle"
    :title="label"
  >
    <span class="kf-chip__dot" :style="{ backgroundColor: chipColor }"></span>
    {{ label }}
  </span>
</template>

<script>
import {
  STATUS_COLOR,
  DEFAULT_STATUS_COLOR,
  displayLabel,
} from "@/app-js/karrofin/constants";

export default {
  name: "KfStatusChip",
  props: {
    status: { type: String, default: "" },
    small: { type: Boolean, default: false },
    /**
     * Override the colour for this chip.
     *
     * For a vocabulary that is not the shared one. STATUS_COLOR is keyed on the
     * bare status string, so two different domains that both say "approved" --
     * a loan and a piece of copy -- would otherwise be forced to look the same.
     * Callers with their own set pass their own map's value.
     */
    color: { type: String, default: "" },
  },
  computed: {
    chipColor() {
      return this.color || STATUS_COLOR[this.status] || DEFAULT_STATUS_COLOR;
    },
    label() {
      // Two display-only rewrites. The `status` prop keeps the server's exact
      // string -- it is what gets sent back as a filter -- so neither of these
      // may change the value, only how it reads.
      //
      //  1. Delivery sub-statuses are stored snake_case server-side
      //     (e.g. "link_clicked"); render them readably.
      //  2. The pipeline stages arrive as "Approved By NBFC" and friends. The
      //     panel no longer surfaces lenders, so the trailing " By NBFC" is
      //     dropped and the stage reads as just "Approved".
      //  3. A few values have a plainer name on screen (DISPLAY_LABEL) --
      //     "EC1 Pending" reads as "Sign up".
      return displayLabel(
        String(this.status)
          .replace(/_/g, " ")
          .replace(/\s*By NBFC$/i, "")
          .trim()
      );
    },
    chipStyle() {
      // The fill used to be "14" (8%), which on the old blue-grey cards was
      // enough to see. On a white card it all but vanished and the pill read as
      // loose coloured text, so it is now 12% with a firmer border.
      return {
        color: this.chipColor,
        backgroundColor: this.chipColor + "1F",
        borderColor: this.chipColor + "47",
      };
    },
  },
};
</script>

<style scoped>
.kf-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 20px;
  border: 1px solid transparent;
  font-size: var(--kf-fs-meta);
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;
  text-transform: capitalize;
}
.kf-chip--sm {
  font-size: var(--kf-fs-label);
  padding: 2px 8px;
}
.kf-chip__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.kf-dash {
  color: #000;
}
</style>
